import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from './use-toast';

export const TIPO_DOCUMENTO_OPTIONS = [
  { value: 'relatorio', label: 'Relatório' },
  { value: 'foto', label: 'Foto' },
  { value: 'laudo', label: 'Laudo' },
  { value: 'art', label: 'ART' },
  { value: 'certificado', label: 'Certificado' },
  { value: 'evidencia_complementar', label: 'Evidência complementar' },
] as const;

export type TipoDocumento = typeof TIPO_DOCUMENTO_OPTIONS[number]['value'];

export const ACCEPTED_MIME = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
export const ACCEPTED_EXT = '.pdf,.jpg,.jpeg,.png';
export const MAX_BYTES = 10 * 1024 * 1024;
const BUCKET = 'quality-evidences';

export interface QualityEvidencia {
  id: string;
  execucao_id: string;
  contrato_id: string;
  arquivo_path: string;
  nome_arquivo: string;
  tipo_documento: TipoDocumento;
  descricao: string | null;
  tamanho_bytes: number;
  mime_type: string;
  uploaded_by: string;
  uploaded_at: string;
}

export interface PendingEvidencia {
  id: string; // local uid
  file: File;
  tipo_documento: TipoDocumento;
  descricao?: string;
  error?: string;
}

export function validateFile(file: File): string | null {
  if (file.size > MAX_BYTES) return `Arquivo excede 10 MB (${(file.size / 1024 / 1024).toFixed(1)} MB).`;
  const mime = (file.type || '').toLowerCase();
  if (!ACCEPTED_MIME.includes(mime)) {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext || '')) {
      return 'Formato não suportado. Use PDF, JPG ou PNG.';
    }
  }
  return null;
}

function sanitize(name: string) {
  return name.replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 120);
}

/** List evidencias of an execução (real-time on demand). */
export function useQualityEvidencias(execucaoId: string | null) {
  return useQuery({
    queryKey: ['quality-evidencias', execucaoId],
    queryFn: async () => {
      if (!execucaoId) return [] as QualityEvidencia[];
      const { data, error } = await supabase
        .from('emp_quality_evidencias' as any)
        .select('*')
        .eq('execucao_id', execucaoId)
        .order('uploaded_at', { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as QualityEvidencia[];
    },
    enabled: !!execucaoId,
  });
}

/** Upload a single pending file, register row, return inserted row id. */
async function uploadOne(
  pending: PendingEvidencia,
  ctx: { execucaoId: string; contratoId: string; userId: string },
): Promise<string> {
  const safeName = sanitize(pending.file.name);
  const path = `${ctx.contratoId}/${ctx.execucaoId}/${crypto.randomUUID()}_${safeName}`;

  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, pending.file, {
      contentType: pending.file.type || 'application/octet-stream',
      cacheControl: '3600',
      upsert: false,
    });
  if (upErr) throw new Error(`Falha no upload de "${pending.file.name}": ${upErr.message}`);

  const { data: row, error: insErr } = await supabase
    .from('emp_quality_evidencias' as any)
    .insert({
      execucao_id: ctx.execucaoId,
      contrato_id: ctx.contratoId,
      arquivo_path: path,
      nome_arquivo: pending.file.name,
      tipo_documento: pending.tipo_documento,
      descricao: pending.descricao?.trim() || null,
      tamanho_bytes: pending.file.size,
      mime_type: (pending.file.type || 'application/octet-stream').toLowerCase(),
      uploaded_by: ctx.userId,
    })
    .select('id')
    .single();

  if (insErr) {
    // Best-effort rollback do storage
    await supabase.storage.from(BUCKET).remove([path]).catch(() => {});
    throw new Error(`Falha ao registrar evidência: ${insErr.message}`);
  }
  return (row as any).id as string;
}

/** Bulk upload helper exported to be composed with the quality-gate mutation. */
export async function uploadQualityEvidencias(
  pendings: PendingEvidencia[],
  ctx: { execucaoId: string; contratoId: string; userId: string },
): Promise<string[]> {
  const ids: string[] = [];
  for (const p of pendings) {
    // Sequential to keep error reporting clean and respect storage rate limits
    const id = await uploadOne(p, ctx);
    ids.push(id);
  }
  return ids;
}

/** Generate a short-lived signed URL for download/preview. */
export async function getSignedEvidenciaUrl(path: string, expiresInSec = 300): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, expiresInSec);
  if (error) throw error;
  return data.signedUrl;
}

/** Delete an evidence (RLS allows only uploader pre-validation). */
export function useDeleteQualityEvidencia() {
  const { toast } = useToast();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ev: QualityEvidencia) => {
      const { error: delDb } = await supabase
        .from('emp_quality_evidencias' as any)
        .delete()
        .eq('id', ev.id);
      if (delDb) throw delDb;
      await supabase.storage.from('quality-evidences').remove([ev.arquivo_path]).catch(() => {});
      return ev;
    },
    onSuccess: (ev) => {
      qc.invalidateQueries({ queryKey: ['quality-evidencias', ev.execucao_id] });
      toast({ title: 'Evidência removida' });
    },
    onError: (e: Error) =>
      toast({ title: 'Erro ao remover evidência', description: e.message, variant: 'destructive' }),
  });
}

export function useAuthUserId() {
  const { user } = useAuth();
  return user?.id ?? null;
}
